/**
 * Home da PLATAFORMA — a vitrine multi-curso, anterior à home de um curso.
 *
 * Renderiza para visitante anônimo e para usuário logado. O que muda entre os
 * dois é apenas o enriquecimento: sem sessão, nenhum curso aparece como
 * matriculado e a seção "continue de onde parou" não existe.
 *
 * Regra importante: nada aqui concede acesso. `enrolled` é dica de UI; o portão
 * real é o JWT validado pelo backend a cada request.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useBodyThemeClass } from '../../hooks/useBodyThemeClass';
import { usePlatformCourses } from './usePlatformCourses';
import type { ResolvedCourse } from './mergeCourses';
import { formatEndDate } from '../../utils/accessDate';
import PlatformSidebar from './PlatformSidebar';
import PlatformTopbar from './PlatformTopbar';
import CourseHero from './CourseHero';
import CourseCarousel from './CourseCarousel';
import CourseCard from './CourseCard';
import ContinueWatching from './ContinueWatching';

const THEME_CLASS = 'medverse-theme';

export interface PlatformHomeProps {
  /** Entrar no curso indicado (o token já é desse curso). */
  onEnterCourse: (slug: string) => void;
  /** Abrir a tela de login, opcionalmente lembrando o curso pretendido. */
  onRequestLogin: (slug?: string) => void;
  /** Abrir a administração (entra no curso atual e vai para a aba admin). */
  onOpenAdmin?: (slug: string) => void;
  /** Abrir configurações (entra no curso atual e vai para a tela de settings). */
  onOpenSettings?: (slug: string) => void;
}

const PlatformHome: React.FC<PlatformHomeProps> = ({
  onEnterCourse,
  onRequestLogin,
  onOpenAdmin,
  onOpenSettings,
}) => {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const { courses, featured } = usePlatformCourses({
    isAuthenticated,
    currentTopicId: user?.topicId,
  });

  const [notices, setNotices] = useState<Record<string, string>>({});
  const [navOpen, setNavOpen] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  useBodyThemeClass(THEME_CLASS);

  const closeNav = useCallback(() => setNavOpen(false), []);

  useEffect(() => {
    if (!navOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navOpen]);

  const setNotice = useCallback((slug: string, message: string | null) => {
    setNotices((prev) => {
      if (message === null) {
        const { [slug]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [slug]: message };
    });
  }, []);

  const handleCourseClick = useCallback(
    async (course: ResolvedCourse) => {
      setNotice(course.slug, null);

      // Produto fora da plataforma: só navega, na aba atual. Não passa por
      // login nem matrícula — quem decide acesso é o outro site.
      if (course.externalUrl) {
        window.location.assign(course.externalUrl);
        return;
      }

      // (0) Sem conteúdo no banco: nada a acessar.
      if (course.status === 'soon') {
        setNotice(course.slug, t('platform.soonDesc'));
        return;
      }

      // (a) Visitante: manda para o login lembrando o curso pretendido, para
      // voltar direto nele depois de autenticar.
      if (!isAuthenticated) {
        onRequestLogin(course.slug);
        return;
      }

      // (d) Logado, mas sem matrícula neste curso.
      if (!course.enrolled) {
        setNotice(course.slug, t('platform.noAccess'));
        return;
      }

      // Matrícula existe, mas está suspensa ou com o prazo vencido.
      if (course.isBlocked) {
        setNotice(
          course.slug,
          course.accessExpired
            ? course.accessEndDate
              ? t('access.courseEndedOn', { date: formatEndDate(course.accessEndDate) })
              : t('access.courseEnded')
            : t('auth.courseBlocked'),
        );
        return;
      }

      // (b) Tem matrícula ativa: entra. Qualquer curso da conta, sem distinção —
      // aqui ficava a reemissão do token, que sumiu junto com a ideia de "curso
      // do token".
      onEnterCourse(course.slug);
    },
    [isAuthenticated, onEnterCourse, onRequestLogin, setNotice, t],
  );

  const handleExplore = useCallback(() => {
    if (!isAuthenticated) {
      onRequestLogin();
      return;
    }
    carouselRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [isAuthenticated, onRequestLogin]);

  // Alvo dos atalhos de Administração e Configurações da barra lateral. Cai no
  // primeiro curso disponível quando nenhum está marcado como atual: desde que
  // trocar de curso virou navegação, a vitrine pode ser aberta sem nenhum curso
  // em foco, e sem este fallback os atalhos sumiriam.
  const currentCourse = useMemo(
    () =>
      courses.find((c) => c.isCurrent && c.enrolled && !c.isBlocked) ??
      courses.find((c) => c.enrolled && !c.isBlocked),
    [courses],
  );

  const handleSelectCourse = useCallback(
    (course: ResolvedCourse) => {
      closeNav();
      void handleCourseClick(course);
    },
    [closeNav, handleCourseClick],
  );

  // "Mais informações" leva ao curso em destaque pelo mesmo caminho do card —
  // login para visitante, entrada direta para quem já tem matrícula.
  const handleMoreInfo = useCallback(() => {
    if (featured) void handleCourseClick(featured);
  }, [featured, handleCourseClick]);

  return (
    // O scroll é do <main>, não da página, como no mockup. O overflow-hidden
    // fica aqui (e não no <body>) para que um erro que troque esta árvore por um
    // fallback não deixe a página travada.
    <div className="grid h-dvh grid-cols-1 overflow-hidden bg-mv-bg text-mv-text nav:grid-cols-[244px_1fr]">
      <PlatformSidebar
        courses={courses}
        onSelect={handleSelectCourse}
        isOpen={navOpen}
        onClose={closeNav}
        showAccessState={isAuthenticated}
        onAdmin={
          user?.isAdmin && currentCourse
            ? () => onOpenAdmin?.(currentCourse.slug)
            : undefined
        }
        onSettings={
          currentCourse ? () => onOpenSettings?.(currentCourse.slug) : undefined
        }
      />

      {navOpen && (
        <div
          onClick={closeNav}
          aria-hidden="true"
          className="fixed inset-0 z-30 bg-[#050816]/60 backdrop-blur-[2px] nav:hidden"
        />
      )}

      {/* id: o mesmo do <main> do CourseApp — é o que os modais globais travam
          (ver LegalConsentModal). Os dois nunca estão montados juntos. */}
      <main
        id="main-scroll"
        className="no-scrollbar relative min-w-0 overflow-x-hidden overflow-y-auto"
      >
        <PlatformTopbar onOpenNav={() => setNavOpen(true)} isNavOpen={navOpen} />

        {/* Full-bleed: o hero encosta nas bordas do main e as fileiras sobem
            sobre a base dele. */}
        <CourseHero
          backgroundImage={featured?.heroImage}
          onExplore={handleExplore}
          onMoreInfo={featured ? handleMoreInfo : undefined}
        />

        <div ref={carouselRef} className="relative z-[2] -mt-10 scroll-mt-16 pt-1.5 pb-[60px]">
          {/* Sem cursos publicados não há fileira: um título sobre faixa vazia
              seria pior do que a ausência da seção. */}
          {courses.length > 0 && (
            <section className="mt-[34px]">
              <CourseCarousel title={t('platform.coursesTitle')}>
                {courses.map((course) => (
                  <CourseCard
                    key={course.slug}
                    course={course}
                    onClick={handleCourseClick}
                    notice={notices[course.slug] ?? null}
                    showAccessState={isAuthenticated}
                  />
                ))}
              </CourseCarousel>
            </section>
          )}

          {isAuthenticated && currentCourse && (
            <section className="mt-[34px]">
              <ContinueWatching
                onOpenCourse={() => onEnterCourse(currentCourse.slug)}
              />
            </section>
          )}
        </div>
      </main>
    </div>
  );
};

export default PlatformHome;
